import {
    Alert,
    Button,
    Card,
    Checkbox,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    Chip
} from '@mui/material';
import { useEffect, useState } from 'react';
import Iconify from '../components/Iconify';
import Page from '../components/Page';
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user'; 


// --- API HOOK IMPLEMENTATION ---

// Helper function for consistent request handling and JSON parsing
const apiFetch = async (url, options = {}) => {
    const response = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        ...options,
        body: options.body ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
        // Attempt to parse JSON error message, fall back to status text
        const errorText = await response.text();
        let errorMessage = `API failed with status ${response.status}`;
        try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
            // response was not JSON
        }
        throw new Error(errorMessage);
    }
    // Return null for 204 No Content (DELETE), otherwise return parsed JSON
    return response.status === 204 ? null : response.json();
};


function useMailList() {
    const [listItems, setListItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const API_URL = 'https://demo.insollex.com/api/mail-list';

    // 1. GET: Fetch all list entries (with search/pagination support)
    const refetch = async (search = '', page = 1, limit = 10) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ search, page: page.toString(), limit: limit.toString() });
            const data = await apiFetch(`${API_URL}?${params.toString()}`);
            setListItems(data?.data || []);
        } catch (err) {
            setError(err);
            setListItems([]);
        } finally {
            setLoading(false);
        }
    };

    // 2. DELETE: Bulk delete entries
    const deleteItems = async (ids) => {
        if (!Array.isArray(ids) || ids.length === 0) return;
        return apiFetch(`${API_URL}/bulk`, {
            method: 'DELETE',
            body: { ids },
        });
    };
    
    // 3. PUT: Update a single entry (Sends only the necessary fields, including companyName)
    const saveItem = async (item) => {
        const { id, email, firstName, lastName, role, companyName } = item;
        if (!id) throw new Error("ID required for saving item.");
        
        const payload = { email, firstName, lastName, role, companyName }; // Send companyName
        
        return apiFetch(`${API_URL}/${id}`, {
            method: 'PUT',
            body: payload,
        });
    };
    
    // 4. POST: Add a new entry
    const addItem = async (data) => {
        return apiFetch(API_URL, {
            method: 'POST',
            body: data,
        });
    };

    useEffect(() => {
        refetch();
    }, []);

    return { listItems, loading, error, refetch, deleteItems, saveItem, addItem };
}
// --------------------------------------------------------------------------


export default function MailList() {
    const { listItems, loading, error, refetch, deleteItems, saveItem, addItem } = useMailList(); 
    
    const [page, setPage] = useState(0);
    const [order, setOrder] = useState('asc');
    const [selected, setSelected] = useState([]);
    const [orderBy, setOrderBy] = useState('email');
    const [filterName, setFilterName] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [isAddFormOpen, setIsAddFormOpen] = useState(false);
    const [isEditFormOpen, setIsEditFormOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [itemsToDelete, setItemsToDelete] = useState([]);

    const isAdmin = true; 
    
    useEffect(() => {
        if (error) {
            console.error(error);
            setSnackbarMessage(error.message || "Error loading mail list!");
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        }
    }, [error]);

    const TABLE_HEAD = [
        { id: 'email', label: 'Email Address', align: 'left' },
        { id: 'role', label: 'Group/Role', align: 'left' },
        { id: 'firstName', label: 'First Name', align: 'left' },
        { id: 'lastName', label: 'Last Name', align: 'left' },
        { id: 'companyName', label: 'Company Name', align: 'left' }, // ADDED
        { id: '' }, // Action column
    ];

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = listItems.map((item) => item.id);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, itemId) => {
        const selectedIndex = selected.indexOf(itemId);
        let newSelected = [];
        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, itemId);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }
        setSelected(newSelected);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        refetch(filterName, newPage + 1, rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        refetch(filterName, 1, newRowsPerPage);
    };

    const handleFilterByName = (event) => {
        const newFilter = event.target.value;
        setFilterName(newFilter);
        refetch(newFilter, 1, rowsPerPage);
    };

    const applySortFilter = (array, comparator, query) => {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    };

    const getComparator = (order, orderBy) => {
        return order === 'desc'
            ? (a, b) => (a[orderBy] < b[orderBy] ? 1 : -1)
            : (a, b) => (a[orderBy] < b[orderBy] ? -1 : 1);
    };

    const filteredItems = applySortFilter(
        Array.isArray(listItems) ? listItems : [],
        getComparator(order, orderBy),
        ''
    );

    const itemsToDisplay = filteredItems.slice(0, rowsPerPage); 

    // Handle opening/closing forms
    const handleOpenAddForm = () => {
        setIsAddFormOpen(true); 
    };

    const handleCloseAddForm = () => {
        setIsAddFormOpen(false); 
        refetch(filterName, page + 1, rowsPerPage); 
    };

    const handleOpenEditForm = (item) => {
        setEditingItem(item);
        setIsEditFormOpen(true);
    };
    
    const handleCloseEditForm = () => {
        setEditingItem(null);
        setIsEditFormOpen(false);
        refetch(filterName, page + 1, rowsPerPage); 
    };

    const handleDeleteItems = async () => {
        try {
            await deleteItems(itemsToDelete); 

            setSnackbarMessage(`Successfully deleted ${itemsToDelete.length} mail list entries!`);
            setSnackbarSeverity('success');
            setSelected([]); 
            refetch(filterName, page + 1, rowsPerPage);
        } catch (error) {
            console.error('Error deleting mail list entries:', error);
            setSnackbarMessage(error.message || 'Error deleting entries!');
            setSnackbarSeverity("error");
        } finally {
            setOpenSnackbar(true);
            setOpenDeleteDialog(false);
        }
    };

    const handleSaveItem = async () => {
        try {
            if (!editingItem?.id) throw new Error("Item ID is missing for update.");
            
            await saveItem(editingItem);

            setSnackbarMessage('Mail list entry updated successfully!');
            setSnackbarSeverity('success');
            handleCloseEditForm();
        } catch (error) {
            console.error('Error saving mail list entry:', error.message);
            setSnackbarMessage(error.message || 'Error saving entry!');
            setSnackbarSeverity("error");
        } finally {
            setOpenSnackbar(true);
        }
    };
    
    const handleDeleteClick = () => {
        if (selected.length === 0) {
            setSnackbarMessage("No entries selected for deletion.");
            setSnackbarSeverity("warning");
            setOpenSnackbar(true);
            return;
        }
    
        setItemsToDelete(selected);
        setOpenDeleteDialog(true);
    };
    
    // Function to update a field in the editing item state
    const handleEditFieldChange = (field, value) => {
        setEditingItem(prev => ({ ...prev, [field]: value }));
    };

    // --- ADD FORM COMPONENT (EMBEDDED) ---
    const AddMailItemForm = ({ open, onClose }) => {
        const [formData, setFormData] = useState({
            email: '',
            firstName: '',
            lastName: '',
            companyName: '', // ADDED
            role: 'client', 
        });

        const handleChange = (e) => {
            const { name, value } = e.target;
            setFormData(prev => ({ ...prev, [name]: value }));
        };

        const handleSubmit = async () => {
            try {
                await addItem(formData); 
                
                setSnackbarMessage('New mail list entry added successfully!');
                setSnackbarSeverity('success');

                setFormData({ email: '', firstName: '', lastName: '', companyName: '', role: 'client' });
                onClose(); 
            } catch (error) {
                setSnackbarMessage(error.message || 'Error adding entry!');
                setSnackbarSeverity("error");
            } finally {
                setOpenSnackbar(true);
            }
        };

        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>Add New Mail List Entry</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            name="email"
                            label="Email Address (Required)"
                            variant="outlined"
                            fullWidth
                            required
                            value={formData.email}
                            onChange={handleChange}
                        />
                        <TextField
                            name="firstName"
                            label="First Name"
                            variant="outlined"
                            fullWidth
                            value={formData.firstName}
                            onChange={handleChange}
                        />
                        <TextField
                            name="lastName"
                            label="Last Name"
                            variant="outlined"
                            fullWidth
                            value={formData.lastName}
                            onChange={handleChange}
                        />
                        <TextField
                            name="companyName"
                            label="Company Name"
                            variant="outlined"
                            fullWidth
                            value={formData.companyName}
                            onChange={handleChange}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Group/Role</InputLabel>
                            <Select
                                name="role"
                                label="Group/Role"
                                value={formData.role}
                                onChange={handleChange}
                            >
                                <MenuItem value="client">Client</MenuItem>
                                <MenuItem value="vendor">Vendor</MenuItem>
                                <MenuItem value="marketing">Marketing</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained">
                        Add Entry
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };
    // --- END ADD FORM COMPONENT ---


    return (
        <Page title="Mail List Admin">
            <Container maxWidth={false} maxHeight={500}>
            {isAdmin ? (
                <>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={3} 
                    >
                        <Typography variant="h4" gutterBottom>
                            Mail List Administration
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handleOpenAddForm}
                            startIcon={<Iconify icon="eva:plus-fill" />}
                        >
                            Add New Entry
                        </Button>
                    </Stack>
        
                    <AddMailItemForm open={isAddFormOpen} onClose={handleCloseAddForm} />
        
                    <Card>
                        <UserListToolbar 
                            numSelected={selected.length} 
                            filterName={filterName} 
                            onFilterName={handleFilterByName} 
                            onDeleteSelected={handleDeleteClick} 
                        />
                        <TableContainer sx={{ maxHeight: 500 }}>
                            <Table>
                                <UserListHead
                                    headLabel={TABLE_HEAD}
                                    order={order}
                                    orderBy={orderBy}
                                    rowCount={listItems.length}
                                    onRequestSort={handleRequestSort}
                                    onSelectAllClick={handleSelectAllClick}
                                />
                                <TableBody>
                                    {listItems.length === 0 && !loading ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                No mail list entries found.
                                            </TableCell>
                                        </TableRow>
                                    ) : loading ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                Loading...
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        itemsToDisplay.map((item) => {
                                            const { id, email, firstName, lastName, role, companyName } = item;
                                            const selectedItem = selected.indexOf(id) !== -1;
                                            return (
                                                <TableRow hover key={id} selected={selectedItem}>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            checked={selectedItem}
                                                            onClick={(event) => handleClick(event, id)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{email}</TableCell>
                                                    <TableCell>{role || 'Default'}</TableCell>
                                                    <TableCell>{firstName || '-'}</TableCell>
                                                    <TableCell>{lastName || '-'}</TableCell>
                                                    <TableCell>{companyName || '-'}</TableCell> {/* ADDED */}
                                                    
                                                    {/* Action column */}
                                                    <TableCell align="right">
                                                        <Button size="small" onClick={() => handleOpenEditForm(item)}>Edit</Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
        
                        {/* Pagination */}
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={listItems.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Card>
        
                    {/* Edit Mail List Item Dialog */}
                    <Dialog open={isEditFormOpen} onClose={handleCloseEditForm}>
                        <DialogTitle>Edit Mail List Entry: {editingItem?.email}</DialogTitle>
                        <DialogContent>
                            <TextField
                                label="Email Address"
                                variant="outlined"
                                fullWidth
                                value={editingItem?.email || ''}
                                onChange={(e) => handleEditFieldChange('email', e.target.value)}
                                sx={{ mb: 2, mt: 1 }}
                            />
                            <TextField
                                label="First Name"
                                variant="outlined"
                                fullWidth
                                value={editingItem?.firstName || ''}
                                onChange={(e) => handleEditFieldChange('firstName', e.target.value)}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Last Name"
                                variant="outlined"
                                fullWidth
                                value={editingItem?.lastName || ''}
                                onChange={(e) => handleEditFieldChange('lastName', e.target.value)}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Company Name"
                                variant="outlined"
                                fullWidth
                                value={editingItem?.companyName || ''} // ADDED
                                onChange={(e) => handleEditFieldChange('companyName', e.target.value)} // ADDED
                                sx={{ mb: 2 }}
                            />
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Group/Role</InputLabel>
                                <Select
                                    value={editingItem?.role || ''}
                                    label="Group/Role"
                                    onChange={(e) => handleEditFieldChange('role', e.target.value)}
                                >
                                    <MenuItem value="client">Client</MenuItem>
                                    <MenuItem value="vendor">Vendor</MenuItem>
                                    <MenuItem value="marketing">Marketing</MenuItem>
                                </Select>
                            </FormControl>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseEditForm} color="secondary">
                                Cancel
                            </Button>
                            <Button onClick={handleSaveItem} color="primary">
                                Save Changes
                            </Button>
                        </DialogActions>
                    </Dialog>
        
                    {/* Delete Confirmation Dialog */}
                    <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                        <DialogTitle>
                            Are you sure you want to delete {itemsToDelete.length} mail list entry(s)?
                        </DialogTitle>
                        <DialogActions>
                            <Button onClick={() => setOpenDeleteDialog(false)} color="secondary">
                                Cancel
                            </Button>
                            <Button onClick={handleDeleteItems} color="error">
                                Confirm Delete
                            </Button>
                        </DialogActions>
                    </Dialog>
        
                    <Stack direction="row" justifyContent="flex-end" spacing={2} mt={2}>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleDeleteClick}
                            startIcon={<Iconify icon="eva:trash-2-outline" />}
                            disabled={selected.length === 0}
                        >
                            Delete Selected
                        </Button>
                    </Stack>
                </>
            ) : (
                <Typography variant="h6" color="error" align="center" mt={5}>
                    Access Denied. Only Administrators can view the Mail List.
                </Typography>
            )}
        
            {/* Snackbar for notifications */}
            <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)}>
                <Alert severity={snackbarSeverity} onClose={() => setOpenSnackbar(false)}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            </Container>
        </Page>
    );
}